import { Component, Input, OnInit, OnDestroy, OnChanges, SimpleChanges } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { Store } from '@ngxs/store';
import { Subject, takeUntil, debounceTime, Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';
import { RequestActions } from '../../store/action/request.action';
import { IORD_Arm, PatchORDArmDto } from '../../interfaces/request.interface';
import { OptionState } from '../../store/state/options.state';

interface ToolSizeOption {
    value: number;
    label: string;
    productLines: string[];
}

const ALL_TOOL_SIZES: ToolSizeOption[] = [
    { value: 400, label: '400', productLines: ['NBR'] },
    { value: 475, label: '475', productLines: ['TDR'] },
    { value: 500, label: '500', productLines: ['NBR'] },
    { value: 550, label: '550', productLines: ['TDR'] },
    { value: 600, label: '600', productLines: ['NBR'] },
    { value: 650, label: '650', productLines: ['NBR'] },
    { value: 675, label: '675', productLines: ['Ho Spho'] },
    { value: 700, label: '700', productLines: ['NBR'] },
    { value: 725, label: '725', productLines: ['TDR'] },
    { value: 800, label: '800', productLines: ['NBR', 'XR'] },
    { value: 850, label: '850', productLines: ['Ho Spho'] },
    { value: 1000, label: '1000', productLines: ['NBR', 'XR'] },
    { value: 1200, label: '1200', productLines: ['Ho Spho', 'NBR', 'XR'] },
    { value: 1400, label: '1400', productLines: ['NBR', 'XR'] },
    { value: 1600, label: '1600', productLines: ['XR'] },
    { value: 1700, label: '1700', productLines: ['NBR', 'Ho Spho'] },
    { value: 1800, label: '1800', productLines: ['XR'] },
    { value: 2200, label: '2200', productLines: ['NBR'] },
    { value: 4.75, label: '4 3/4', productLines: ['Conventional', 'FCS', 'Surface Equipment', 'Other'] },
    { value: 5.5, label: '5 1/2', productLines: ['RockStrong', 'Conventional', 'Surface Equipment', 'Other'] },
    { value: 6.75, label: '6 3/4', productLines: ['RockStrong', 'Conventional', 'FCS', 'Surface Equipment', 'Other'] },
    { value: 7.25, label: '7 1/4', productLines: ['Conventional', 'Surface Equipment', 'Other', 'Sponge'] },
    { value: 8, label: '8', productLines: ['Conventional', 'FCS', 'Surface Equipment', 'Other'] },
    { value: 0, label: 'Other', productLines: ['NBR', 'TDR', 'Ho Spho', 'XR', 'Conventional', 'RockStrong', 'FCS', 'RockSwift', 'Surface Equipment', 'Other', 'Sponge'] }
];

@Component({
    selector: 'app-arm-data',
    templateUrl: './arm-data.component.html',
    styleUrl: './arm-data.component.css',
    standalone: false
})
export class ArmDataComponent implements OnInit, OnDestroy, OnChanges {
    @Input() draftId?: number;
    @Input() armInitial?: IORD_Arm | undefined;
    @Input() readonly: boolean = false;
    @Input() ProductLine$?: Observable<string>;

    public armForm!: FormGroup;
    private destroy$ = new Subject<void>();

    public sizeOptions: ToolSizeOption[] = [];
    public hardfacingOptions$!: Observable<any[]>;

    public levelOptions = [
        { id: 2, name: '2 - No Cutters' },
        { id: 3, name: '3' },
        { id: 4, name: '4 - No Cutters' },
        { id: 5, name: '5 - Detached U/S' },
        { id: 6, name: '6 - Full Build' },
        { id: 7, name: '7 - Finished Mold' }
    ];

    public inputModeDecimal: { [key: string]: string } = { inputmode: 'decimal' };
    public decimals = 3;
    public format = 'n3';

    constructor(
        private fb: FormBuilder,
        private store: Store
    ) {
        this.armForm = this.fb.group({
            size: [null],
            type: [null],
            bitFeatures: [null],
            level: [null],
            gaugeLength: [null],
            hardfacing: [null],
            openingSize: [0],
            cutterRow: [0],
            newArm: [false]
        });
    }

    ngOnInit(): void {
        // Filter body materials to HARDFACING location, exclude obsolete
        this.hardfacingOptions$ = this.store.select(OptionState.bodyMaterial).pipe(
            map((materials: any[]) => (materials ?? []).filter(m => {
                const location = (m?.location ?? m?.Location ?? '').toString().toUpperCase();
                return location === 'HARDFACING' && !(m?.obsolete ?? m?.Obsolete ?? false);
            }))
        );

        if (this.armInitial) {
            this.armForm.patchValue({
                size: this.armInitial.size ?? null,
                type: this.armInitial.type ?? null,
                bitFeatures: this.armInitial.bitFeatures ?? null,
                level: this.armInitial.level ?? null,
                gaugeLength: this.armInitial.gaugeLength ?? null,
                hardfacing: this.armInitial.hardfacing ?? null,
                openingSize: this.armInitial.openingSize ?? 0,
                cutterRow: this.armInitial.cutterRow ?? 0,
                newArm: this.armInitial.newArm ?? false
            }, { emitEvent: false });
        }

        // Filter size options based on product line
        (this.ProductLine$ ?? of('NA')).pipe(
            takeUntil(this.destroy$)
        ).subscribe(productLine => {
            this.sizeOptions = ALL_TOOL_SIZES.filter(ts =>
                ts.productLines.some(pl => pl.toLowerCase() === (productLine ?? '').toLowerCase())
            );

            // If current size value is not in filtered options, reset it
            const currentSize = this.armForm.get('size')?.value;
            if (currentSize != null && !this.sizeOptions.some(o => o.value === currentSize)) {
                this.armForm.patchValue({ size: null }, { emitEvent: false });
            }
        });

        this.armForm.valueChanges.pipe(
            debounceTime(200),
            takeUntil(this.destroy$)
        ).subscribe(() => {
            const armDto = this.buildArmDto();
            this.store.dispatch(new RequestActions.PatchRequestDraft({
                draftId: this.draftId,
                changes: { arm: armDto }
            }));
            this.store.dispatch(new RequestActions.PatchRequest({
                arm: this.buildArmFromForm() as any
            }));
        });
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['armInitial'] && !changes['armInitial'].firstChange) {
            const arm = changes['armInitial'].currentValue as IORD_Arm | undefined;
            if (arm) {
                this.armForm.patchValue({
                    size: arm.size ?? null,
                    type: arm.type ?? null,
                    bitFeatures: arm.bitFeatures ?? null,
                    level: arm.level ?? null,
                    gaugeLength: arm.gaugeLength ?? null,
                    hardfacing: arm.hardfacing ?? null,
                    openingSize: arm.openingSize ?? 0,
                    cutterRow: arm.cutterRow ?? 0,
                    newArm: arm.newArm ?? false
                }, { emitEvent: false });
            }
        }
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    private buildArmDto(): PatchORDArmDto {
        const formValues = this.armForm.value;
        const existingId = this.armInitial?.id ?? 0;
        return {
            id: existingId,
            orderId: this.draftId ?? 0,
            size: formValues.size ?? null,
            type: formValues.type ?? null,
            bitFeatures: formValues.bitFeatures ?? null,
            level: formValues.level ?? null,
            gaugeLength: formValues.gaugeLength ?? null,
            hardfacing: formValues.hardfacing ?? null,
            openingSize: formValues.openingSize ?? 0,
            cutterRow: formValues.cutterRow ?? 0,
            newArm: formValues.newArm ?? false
        };
    }

    private buildArmFromForm(): IORD_Arm {
        const formValues = this.armForm.value;
        const existingId = this.armInitial?.id ?? 0;
        return {
            id: existingId ?? 0,
            orderId: this.draftId ?? 0,
            size: formValues.size,
            type: formValues.type,
            bitFeatures: formValues.bitFeatures,
            level: formValues.level,
            gaugeLength: formValues.gaugeLength,
            hardfacing: formValues.hardfacing,
            openingSize: formValues.openingSize,
            cutterRow: formValues.cutterRow,
            newArm: formValues.newArm
        };
    }
}
